import {createBrowserRouter} from "react-router";
import {FC, Suspense} from "react";

import ErrorPage from "pages/ErrorPage";
import GlobalLayout from "layouts/GlobalLayout";
import ErrorPage403 from "pages/error/403";
import ErrorPage404 from "pages/error/404";
import RoleGuard from "components/RoleGuard";
import LoadingScreen from "components/ScreenState/Loading";
import CenterContainer from "components/CenterContainer";
import pages, {DashboardWrapperPage, Page, PageGroup} from "./pages";
import {Role} from "types/global";
import RedirectToDefault from "layouts/RedirectToDefault";
import {isPageGroupWithChildren} from "./getNavigation";

const withSuspense = (Component: FC) => {
  const Wrapped: FC = () => (
    <Suspense
      fallback={
        <CenterContainer>
          <LoadingScreen />
        </CenterContainer>
      }>
      <Component />
    </Suspense>
  );

  Wrapped.displayName = `WithSuspense(${Component.displayName || Component.name || "Component"})`;
  return Wrapped;
};

const withRoleGuard = (Component: FC, minRole: Role) => {
  const Wrapped: FC = () => (
    <RoleGuard minRole={minRole}>
      <Component />
    </RoleGuard>
  );

  Wrapped.displayName = `WithRoleGuard(${Component.displayName || Component.name || "Component"})`;
  return Wrapped;
};

const buildRoutes = (pages: Array<Page | PageGroup>): any[] => {
  return pages
    .map(page => {
      if ("Component" in page) {
        return {
          path: page.segment,
          Component: withSuspense(withRoleGuard(page.Component, page.minRole)),
        };
      } else if (isPageGroupWithChildren(page)) {
        return {
          path: page.segment,
          children: buildRoutes(page.children),
        };
      }
      return null;
    })
    .filter(Boolean);
};

const router = createBrowserRouter([
  {
    Component: withSuspense(GlobalLayout),
    children: [
      {
        path: "/",
        Component: withSuspense(DashboardWrapperPage),
        errorElement: <ErrorPage />,
        children: [
          {
            index: true,
            Component: RedirectToDefault,
          },
          ...buildRoutes(pages),
        ],
      },
      {path: "/forbidden", Component: ErrorPage403},
      {path: "*", Component: ErrorPage404},
    ],
  },
]);

export default router;
